import {useMemo} from 'react';

import type {TagAttributes} from '../../../../../components/TagAssignment';
import {
  type RegistryCollectionsWithDetailsQueryVariables,
  useRegistryCollectionsWithDetailsQuery,
} from '../../../../../generated/graphql';
import type {ApolloError} from '../../../../../state/graphql/apolloWithAbort';
import {propagateErrorsContext} from '../../../../../util/errors';
import type {FilterItem} from '../../../../common/FilterBar';
import type {RegistrySearchCollectionType} from '../../../common/types';
import {checkRegistryProject} from '../../../common/utils';
import {splitAndSerializeFilters} from '../filterConverter';
import {usePaginatedQueryWithFilters} from './usePaginatedQueryWithFilters';

const FETCH_COLLECTIONS_BATCH_SIZE = 50;

type CollectionsQueryData = ReturnType<
  typeof useRegistryCollectionsWithDetailsQuery
>['data'];

type CollectionEdge = NonNullable<
  NonNullable<
    NonNullable<NonNullable<CollectionsQueryData>['organization']>['orgEntity']
  >['artifactCollections']
>['edges'][number];

type UseCollectionsWithFiltersProps = {
  orgName: string;
  filters: FilterItem[];
  order?: string;
  page: number;
  pageSize: number;
  skip?: boolean;
};

type UseCollectionsWithFiltersResult = {
  collections: RegistrySearchCollectionType[];
  loading: boolean;
  error: ApolloError | undefined;
  totalLoaded: number;
  hasNextPage: boolean;
  refetch: () => void;
};

const getCollectionsResult = (data: CollectionsQueryData) => {
  return data?.organization?.orgEntity?.artifactCollections;
};

const parseCollectionEdges = (
  edges: readonly CollectionEdge[]
): RegistrySearchCollectionType[] => {
  return edges.flatMap(edge => {
    const node = edge.node;
    if (!node) {
      return [];
    }

    const projectName = node.project?.name ?? '';
    const {registryName} = checkRegistryProject(projectName);
    if (!registryName) {
      return [];
    }

    type TagNode = {
      id: string;
      name: string;
      tagCategoryName: string;
      attributes: TagAttributes;
    };
    const tags =
      node.tags?.edges
        ?.map((tagEdge: {node: TagNode}) => tagEdge?.node)
        .filter((tag): tag is TagNode => tag != null)
        .map(tag => ({
          id: tag.id,
          name: tag.name,
          tagCategoryName: tag.tagCategoryName,
          attributes: tag.attributes,
        })) ?? [];

    const registryIcon = node.project?.icon
      ? {
          name: node.project.icon.name ?? '',
          color: node.project.icon.color ?? '',
        }
      : undefined;

    return [
      {
        collectionId: node.id,
        collectionName: node.name,
        description: node.description ?? undefined,
        createdAt: node.createdAt,
        lastUpdatedAt: node.updatedAt ?? node.createdAt,
        projectId: node.project?.id ?? '',
        projectName,
        entityName: node.project?.entity?.name ?? '',
        registryName,
        artifactTypeName: node.artifactType?.name ?? '',
        tags,
        isPortfolio: node.__typename === 'ArtifactPortfolio',
        isArtifactMatch: false,
        registryIcon,
        protectedArtifactAliases: node.project?.protectedArtifactAliases?.map(
          (a: {alias: string}) => a.alias
        ),
      },
    ];
  });
};

export function useCollectionsWithFilters({
  orgName,
  filters,
  order,
  page,
  pageSize,
  skip,
}: UseCollectionsWithFiltersProps): UseCollectionsWithFiltersResult {
  const fetchBatchSize = Math.max(FETCH_COLLECTIONS_BATCH_SIZE, pageSize);

  const {registryFilter, collectionFilter} = useMemo(
    () => splitAndSerializeFilters(filters, 'collections'),
    [filters]
  );

  const queryResult = useRegistryCollectionsWithDetailsQuery({
    variables: {
      organization: orgName,
      registryFilter,
      collectionFilter,
      order,
      perPage: fetchBatchSize,
    },
    skip,
    context: propagateErrorsContext(),
    notifyOnNetworkStatusChange: true,
  });

  const config = useMemo(
    () => ({
      getResult: getCollectionsResult,
      parseEdges: parseCollectionEdges,
      getFetchMoreVariables: (endCursor: string) => ({
        organization: orgName,
        registryFilter,
        collectionFilter,
        order,
        perPage: fetchBatchSize,
        cursor: endCursor,
      }),
    }),
    [orgName, registryFilter, collectionFilter, fetchBatchSize, order]
  );

  const {items, loading, error, totalLoaded, hasNextPage} =
    usePaginatedQueryWithFilters<
      NonNullable<CollectionsQueryData>,
      CollectionEdge,
      RegistrySearchCollectionType,
      RegistryCollectionsWithDetailsQueryVariables
    >(queryResult, config, page, pageSize);

  return {
    collections: items,
    loading,
    error,
    totalLoaded,
    hasNextPage,
    refetch: queryResult.refetch,
  };
}
